import { useCallback, useState } from "react";
import { api } from "../../lib/api";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import { REVIEW_PATHS, REQUEST_STATES, TOKEN_QUERY_PARAM } from "../contract";

/**
 * `GET /reviews/request?token=` — everything the review form needs to open.
 *
 * The token is the capability; the API is host-agnostic. The server answers 200 for a
 * form that is still open AND for one that is already `submitted` or `expired` (so the
 * screen can explain which), and 404 `REVIEW_TOKEN_UNKNOWN` for a token that names no
 * request — absent, malformed or unknown, one uniform answer. That 404 is a genuine
 * `loadError` here (the form cannot say more than "this link is not valid"), and
 * `ReviewForm` renders it through `reviewErrorText`, so the sentence is translated.
 *
 * A missing token is refused BEFORE any request is made: `ReviewForm` renders its own
 * "no token" panel for that and never mounts this hook with an empty string, but the
 * guard stays here so the hook cannot be made to fire a request that can only 404.
 *
 * SHAPE-GUARDED against a 200 that is not a request context (`state` outside
 * `REQUEST_STATES`, no `practitioner`) — the production proxy's index.html answer.
 *
 * `applySubmitted(review)` is how the form flips its own context to the `submitted`
 * state after a 201 without a second round trip: the server's answer to the very same
 * GET, one instant later, would carry exactly `{state: "submitted", review}`.
 *
 * @param {string} token the review token from the mail link's query string
 */
export default function useReviewRequest(token) {
  const [context, setContext] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    const { data } = await api.get(REVIEW_PATHS.request, { params: { [TOKEN_QUERY_PARAM]: token } });
    if (!data || typeof data !== "object" || !REQUEST_STATES.includes(data.state)
        || typeof data.program_enabled !== "boolean"
        || !data.practitioner || typeof data.practitioner !== "object") {
      throw payloadShapeError();
    }
    setContext(data);
  }, [token]);

  const { loading, loadError, reload } = useAdminLoad(load);

  const applySubmitted = useCallback((review) => {
    setContext((prev) => (prev ? { ...prev, state: "submitted", review } : prev));
  }, []);

  return { context, loading, loadError, reload, applySubmitted };
}
